import React from "react";

/**
 * Componente PageHeader reutilizable
 * 
 * @param {Object} props
 * @param {Function} props.onBack - Función a ejecutar al hacer clic en "Volver"
 * @param {string} props.title - Título principal del header
 * @param {string} props.subtitle - Subtítulo/descripción (opcional)
 * @param {boolean} props.isEditMode - Si está en modo edición (para AddEditExpense)
 * @param {string} props.monthName - Nombre del mes (para MonthDetail)
 * @param {Function} props.onEditIncomes - Función para editar ingresos (opcional)
 * @param {Function} props.onDeleteMonth - Función para eliminar mes (opcional)
 * @param {string} props.backLabel - Texto del botón volver (default: "Volver")
 * @param {string} props.backAriaLabel - Aria label del botón volver
 */
const PageHeader = ({
  onBack,
  title,
  subtitle,
  isEditMode,
  monthName,
  onEditIncomes,
  onDeleteMonth,
  backLabel = "Volver",
  backAriaLabel = "Volver"
}) => {
  // Determinar el título según las props
  let displayTitle = title;
  if (isEditMode !== undefined) {
    displayTitle = isEditMode ? "Editar gasto" : "Agregar gasto";
  } else if (monthName) {
    displayTitle = monthName;
  }

  // Determinar el subtítulo según las props
  let displaySubtitle = subtitle;
  if (isEditMode !== undefined && !subtitle) {
    displaySubtitle = "Registra un gasto del mes";
  } else if (monthName && !subtitle) {
    displaySubtitle = "Detalle del mes";
  }

  return (
    <div className="mb-8">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mb-4 transition-colors group"
        aria-label={backAriaLabel}
      >
        <svg 
          className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        {backLabel}
      </button>
      
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2 tracking-tight">
            {displayTitle}
          </h1>
          {displaySubtitle && (
            <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
              {displaySubtitle}
            </p>
          )}
        </div>
        
        {/* Botones de acción adicionales (para MonthDetail) */}
        {(onEditIncomes || onDeleteMonth) && (
          <div className="flex gap-2">
            {onEditIncomes && (
              <button
                onClick={onEditIncomes}
                className="px-4 py-2 bg-green-100 hover:bg-green-200 text-green-700 font-semibold rounded-xl transition-colors flex items-center gap-2"
                aria-label="Editar ingresos"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Editar Ingresos
              </button>
            )}
            {onDeleteMonth && (
              <button
                onClick={onDeleteMonth}
                className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 font-semibold rounded-xl transition-colors flex items-center gap-2"
                aria-label="Eliminar mes"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHeader;
